import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { CreateAndLinkSpeakingSessionFormValues, schema } from './validation';
import { lrwSessionStore, speakingSessionStore } from 'store';
import { useIntParam, useNavigationContext, useSafeMutation, useServerErrors } from 'hooks';
import { useCallback, useMemo } from 'react';
import { parseServerErrors } from 'utils';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { ROUTING_PATHS } from 'const';

export const useLinkNewSpeakingSession = () => {
  const lrwSessionId = useIntParam('sessionId');
  const location = useLocation();
  const navigate = useNavigate();
  const { locationId } = useNavigationContext();
  const { lrwSessionDetails } = ROUTING_PATHS;

  const form = useForm<CreateAndLinkSpeakingSessionFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {},
  });

  const { data: lrwSession } = lrwSessionStore.service.lrwSessionApi.useGetLrwSessionDetailsQuery({
    lrwSessionId,
    locationId,
  });

  const examDate = lrwSession?.date;

  const { data: availableVenues, isFetching: isFetchingVenues } =
    speakingSessionStore.service.useGetAvailableVenuesForSpeakingSessionQuery(
      {
        sessionDate: examDate!,
        locationId: locationId!,
      },
      { skip: !examDate || !locationId }
    );

  const [
    createAndLinkSpeakingSession,
    { isLoading: isCreatingAndLinking, isSuccess: createdAndLinked, error: createAndLinkErrors },
  ] = lrwSessionStore.service.useCreateAndLinkSpeakingSessionMutation();

  const goToLrwSessionDetails = useCallback(
    (sessionId: number) =>
      navigate(generatePath(lrwSessionDetails.absolutePath, { sessionId }), {
        state: location.state,
      }),
    [location.state, navigate, lrwSessionDetails.absolutePath]
  );

  const { execute: createAndLink } = useSafeMutation(createAndLinkSpeakingSession, {
    successToastMessage: 'Changes saved successfully',
    onSuccess: () => goToLrwSessionDetails(lrwSessionId),
  });

  const serverErrors = useMemo(() => parseServerErrors(createAndLinkErrors), [createAndLinkErrors]);

  const { generalErrors, clearServerErrors } = useServerErrors(
    form.getValues(),
    serverErrors,
    form.setError
  );

  const handleSubmit = (data: CreateAndLinkSpeakingSessionFormValues) => {
    clearServerErrors();

    createAndLink({
      lrwSessionId,
      request: {
        venueId: data.venueId,
        testTakerAdmissionTimeMinutes: data.testTakerAdmissionTimeMinutes ?? 0,
      },
    });
  };

  return {
    lrwSession,
    form,
    venueOptions: availableVenues?.map((x) => ({ text: x.name, value: x.id })) ?? [],
    isFetchingVenues,
    isCreatingAndLinking,
    createdAndLinked,
    generalErrors,
    handleSubmit,
  };
};
